import {Observable, Subscription} from 'rxjs';
import {filter, map, switchMap, tap} from 'rxjs/operators';
import {ChangeDetectionStrategy, Component, Inject, Input, OnInit} from '@angular/core';
import {Store} from '@ngxs/store';
import {EChartsCoreOption} from 'echarts/core';
import {TreeSeriesNodeItemOption} from 'echarts/types/src/chart/tree/TreeSeries';
import {brainImage, symbolLarge, symbolLargeLarge, symbolLargeSmall, symbolSmall, symbolSmallSmall} from '../../symbols/dashboard-genetics-base64-images.symbols';
import {DashboardGeneticsRetrieveState} from '../../states/dashboard-genetics-retrieve/dashboard-genetics-retrieve.state';
import {DashboardGeneticsRetrieveStateModel} from '../../states/dashboard-genetics-retrieve/dashboard-genetics-retrieve.models';
import {LoadGeneticData} from '../../states/dashboard-genetics-retrieve/dashboard-genetics-retrieve.actions';
import {PROGRESS_STATUSES} from '../../../shared/symbols';
import {WINDOW} from '../../../universal/window/window.service';
import {geneticsGenerateTooltipMessage} from '../../symbols/dashboard-genetics-general.symbols';
import {GeneticData, SNP} from '../../types/dashboard-genetics-general.types';
import {ProcessedGeneticFileStatus} from '../../types/dashboard-genetics-processing.types';
import { NgxEchartsDirective } from 'ngx-echarts';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AsyncPipe } from '@angular/common';


@Component({
    selector: 'app-dashboard-genetics-chart',
    templateUrl: './dashboard-genetics-chart.component.html',
    styleUrls: ['./dashboard-genetics-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgxEchartsDirective, MatProgressSpinner, AsyncPipe]
})
export class DashboardGeneticsChartComponent implements OnInit {
  public readonly loadGeneticsProgress$ = this.store.select(DashboardGeneticsRetrieveState.loadGeneticsProgress);
  public readonly geneticData$ = this.store.select(DashboardGeneticsRetrieveState.geneticData);

  @Input() geneticStatus$: Observable<DashboardGeneticsRetrieveStateModel['mostActualStatus']>;

  /** Contain a dynamically generated list of notes (one for each gene). */
  private nodesDict: { [id: string]: TreeSeriesNodeItemOption };

  /** Contain component subscriptions. Will be unsubscribed on destroy. */
  private subscription = new Subscription();

  /** Default setup for chart. Used for dynamical config generation with provided data. */
  private readonly defaultChartConfig: EChartsCoreOption = {
    tooltip: {
      trigger: 'item',
      borderRadius: 0,
      borderColor: '#000000',
      borderWidth: 1,
      padding: 12,
      enterable: true,
      formatter: (params) => {
        return params.value;
      },
      position: (point, params, dom, rect, size) => {
        const x = point[0];
        const y = point[1];
        const boxWidth = size.contentSize[0];
        const boxHeight = size.contentSize[1];

        let posX;
        if (x < boxWidth) {
          posX = 5;
        } else {
          posX = x - boxWidth;
        }

        let posY;
        if (y < boxHeight) {
          posY = 5;
        } else {
          posY = y - boxHeight;
        }

        return [posX, posY];
      }
    },
    animationDurationUpdate: 1500,
    series: [
      {
        type: 'tree',
        layout: 'radial',
        legendHoverLink: false,
        lineStyle: {
          width: 3,
          color: '#D5D6E3',
          curveness: 0,
        },
        emphasis: {
          scale: false,
        },
        initialTreeDepth: 1,
      }
    ]
  };

  /** Displays if the tooltip should be shown on gene hover. */
  public showTooltipOnHover: boolean;

  /** Contain default chart config patched with actual genes data. */
  public actualChartConfig$: Observable<EChartsCoreOption>;

  /** Contain all available progress statuses. Saved in local variable for usage in html template. */
  public readonly progressStatuses = PROGRESS_STATUSES;

  /** Contain all available genetic files statuses. Saved in local variable for usage in html template. */
  public readonly geneticFileStatuses = ProcessedGeneticFileStatus;


  constructor(@Inject(WINDOW) private window: Window, private store: Store) {
  }


  ngOnInit(): void {
    // Start load genetic data if submission contain correct genetic data.
    this.subscription.add(
      this.geneticStatus$.pipe(
        filter(status => !!status && status.pipeline_type === this.geneticFileStatuses.EXTRACTED),
      ).subscribe(() => this.store.dispatch(new LoadGeneticData()))
    );

    // Generate config for echart using provided genetic data.
    this.actualChartConfig$ = this.geneticStatus$.pipe(

      // Prevent any changes if submission has no formed data about genes.
      filter(status => status?.pipeline_type === ProcessedGeneticFileStatus.EXTRACTED),

      // Switch to genetic data.
      switchMap(() => this.geneticData$),

      // Prevent any changes if genetic data not presented.
      filter(geneticData => !!geneticData),

      // Mark local variable if tooltip should be showed
      tap(() => this.showTooltipOnHover = this.window.innerWidth > 960),

      // Generate options from provided data and return as new object.
      map(report => {
        const isLargeScreen = this.window.innerWidth > 1200 && this.window.innerHeight > 800;
        const data: TreeSeriesNodeItemOption = {
          id: '0',
          symbolSize: isLargeScreen ? [333, 220] : [200, 125],
          symbol: brainImage,
          children: this.generateNodes(report),
        };
        return {
          ...this.defaultChartConfig,
          series: [{
            ...this.defaultChartConfig.series[0],
            tooltip: {
              show: this.showTooltipOnHover,
            },
            label: {
              show: true,
              position: 'inside',
              align: 'center',
              rotate: 0,
              color: '#2B3357',
              fontSize: isLargeScreen ? 20 : 12,
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 400,
              rich: {
                bold: {
                  fontWeight: 600,
                  color: '#2B3357',
                  fontSize: isLargeScreen ? 20 : 12,
                  fontFamily: '"Montserrat", sans-serif',
                },
                boldFirst: {
                  fontWeight: 600,
                  color: '#2B3357',
                  fontSize: isLargeScreen ? 20 : 12,
                  fontFamily: '"Montserrat", sans-serif',
                  padding: [0, (isLargeScreen ? 60 : 35), 0, 0],
                },
              },
            },
            symbolSize: isLargeScreen ? [50, 50] : [28, 28],
            data: [data],
          }],
        };
      }),
    );
  }

  generateNodes(report: GeneticData): TreeSeriesNodeItemOption[] {
    this.nodesDict = {};
    for (const elem of (report.SNPs as SNP[])) {
      if (elem.patient_score === 0) {
        continue;
      }
      const nodeId = elem.name.split('-')[0];
      if (this.nodesDict[nodeId]) {
        const firstEffect = this.nodesDict[nodeId].name;
        this.nodesDict[nodeId] = {
          ...this.nodesDict[nodeId],
          label: {
            formatter: `{boldFirst|${elem.genotype[0]}}{bold|${elem.genotype[1]}}`,
          },
          symbol: this.getCurrentImageForDoubleSnps((firstEffect as (-1 | 1)), elem.effect_direction),
          symbolRotate: +firstEffect < elem.effect_direction ? 90 : 0,
          value: this.generateDoubleTooltipMessage(elem, (this.nodesDict[nodeId].value as string)),
        };
      } else {
        this.nodesDict[nodeId] = {
          id: nodeId,
          value: this.generateTooltipMessage(elem),
          label: {
            formatter: `{boldFirst|${elem.genotype[0]}}${this.isFullColored(elem.patient_score) ? `{bold|${elem.genotype[1]}}` : elem.genotype[1]}`,
          },
          symbol: this.getCurrentImage(elem.effect_direction, elem.patient_score),
          name: elem.effect_direction,
        };
      }
    }
    return Object.keys(this.nodesDict).map(id => this.nodesDict[id]);
  }

  isFullColored(score: 1 | -1 | 2 | -2): boolean {
    return score === 2 || score === -2;
  }

  getCurrentImage(effect: 1 | -1, score: 1 | -1 | 2 | -2): string {
    if (score === 2 || score === -2) {
      if (effect === 1) {
        return symbolLargeLarge;
      } else if (effect === -1) {
        return symbolSmallSmall;
      }
    } else {
      if (effect === 1) {
        return symbolLarge;
      } else if (effect === -1) {
        return symbolSmall;
      }
    }
  }

  getCurrentImageForDoubleSnps(firstEffect: 1 | -1, secondEffect: 1 | -1): string {
    if (firstEffect !== secondEffect) {
      return symbolLargeSmall;
    } else {
      return firstEffect === 1 ? symbolLargeLarge : symbolSmallSmall;
    }
  }

  generateTooltipMessage(snp: SNP): string {
    return geneticsGenerateTooltipMessage(snp);
  }

  generateDoubleTooltipMessage(snp: SNP, firstTooltip: string): string {
    return `<div class="genotype-tooltip-wrap">
      ${firstTooltip}
      <div class="genotype-tooltip-wrap__split"></div>
      ${this.generateTooltipMessage(snp)}
    </div>`;
  }

  chartClick(e) {
    const id = e?.data?.id;
    if (!this.showTooltipOnHover && id && this.nodesDict[id]) {
      const existedTooltip = document.getElementById('geneticTooltip');
      if (existedTooltip) {
        existedTooltip.innerHTML = this.generateMobileTooltip(id);
      } else {
        const elem = document.createElement('div');
        elem.id = 'geneticTooltip';
        elem.classList.add('gen-tooltip-mobile');
        elem.innerHTML = this.generateMobileTooltip(id);
        document.body.appendChild(elem);
      }
    }
  }

  generateMobileTooltip(id: string): string {
    return `<div class="swipe-button-wrapper">
        <div class="swipe-button"></div>
      </div>
      ${this.nodesDict[id].value}`;
  }
}
